/**
 * Os documentos emitidos do caso, na tela da equipe (E138.F3, GUS-21, ADR-0094): cada versão com o
 * código registrado fora do arquivo, a origem (o padrão da assinatura e os manifestos) e a
 * conferência de um arquivo recebido contra aquela versão. Só a etapa final serve de referência,
 * e só ela é listada. Desde a E145.F2 (ADR-0099 §2.6), cada versão tem também o link do QR.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import { CartaoDaAbertura } from '@/components/Abertura/PecasDaAbertura';
import { ptBR } from '@/i18n/pt-BR';
import { listarEmissoes, type EmissaoData } from '@/lib/emissoes';
import { formatarDataHora } from '@/lib/trilha_format';

import { AcessoPeloQr } from './AcessoPeloQr';
import { ConferirArquivo } from './ConferirArquivo';

const t = ptBR.integridadeDoArquivo;

function origem(emissao: EmissaoData): string {
  const manifestos =
    emissao.manifestos === null
      ? t.documentos.semManifestos
      : t.documentos.manifestos.replace('{n}', String(emissao.manifestos.length));
  return `${t.documentos.assinatura[emissao.assinatura]} · ${manifestos}`;
}

interface EmissaoProps {
  client: AxiosInstance;
  caseId: string;
  emissao: EmissaoData;
}

function Emissao({ client, caseId, emissao }: EmissaoProps): JSX.Element {
  const documento = t.documentos.documento.replace(
    '{ccb}',
    emissao.ccb_number ?? t.documentos.semNumero,
  );
  return (
    <li
      className="border-b border-border py-sc-3 last:border-b-0"
      data-testid={`emissao-${emissao.id}`}
    >
      <p className="text-body-sm font-semibold text-fg">{documento}</p>
      <p className="text-caption text-fg-muted">
        {t.versao
          .replace('{versao}', String(emissao.versao))
          .replace('{data}', formatarDataHora(emissao.emitido_em))}
      </p>
      <p className="text-caption text-fg-dim">{origem(emissao)}</p>
      <p className="mt-1 text-caption text-fg-dim">
        <span>{t.codigoRegistrado}: </span>
        <span className="break-all font-mono">{emissao.codigo}</span>
      </p>
      <ConferirArquivo
        client={client}
        caseId={caseId}
        documentoId={emissao.source_document_id}
        versao={emissao.versao}
      />
      <AcessoPeloQr
        client={client}
        caseId={caseId}
        emissaoId={emissao.id}
        formato={emissao.formato}
      />
    </li>
  );
}

export interface DocumentosEmitidosProps {
  client: AxiosInstance;
  caseId: string;
}

export function DocumentosEmitidos({ client, caseId }: DocumentosEmitidosProps): JSX.Element {
  const [emissoes, setEmissoes] = useState<EmissaoData[] | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    listarEmissoes(client, caseId)
      .then((lidas) => {
        if (vivo) setEmissoes(lidas.filter((e) => e.etapa === 'final'));
      })
      .catch(() => {
        if (vivo) setErro(true);
      });
    return () => {
      vivo = false;
    };
  }, [client, caseId]);

  return (
    <CartaoDaAbertura titulo={t.documentos.titulo} testId="documentos-emitidos">
      <p className="text-caption text-fg-muted">{t.documentos.subtitulo}</p>
      {erro ? (
        <p role="alert" className="mt-sc-2 text-body-sm text-danger">
          {t.documentos.erro}
        </p>
      ) : emissoes === null ? null : emissoes.length === 0 ? (
        <p className="mt-sc-2 text-body-sm text-fg-muted" data-testid="documentos-emitidos-vazio">
          {t.documentos.vazio}
        </p>
      ) : (
        <ul className="mt-sc-2 flex flex-col">
          {emissoes.map((emissao) => (
            <Emissao key={emissao.id} client={client} caseId={caseId} emissao={emissao} />
          ))}
        </ul>
      )}
    </CartaoDaAbertura>
  );
}
